// Vendor
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Redux
import { selectors } from 'src/features/auth/authSlice';
import {
  cancelReviewEntryRequestThunk,
  getPendingReviewEntryThunk,
  getSpecialtyQueuePendingCountThunk,
  actions as myWorkActions,
  selectors as myWorkSelectors,
  requestReviewEntryThunk,
  REVIEW_ASSIGNED_EVENT,
  useChannel,
  useSpecialtyQueueAccess
} from 'src/features/myWork';
import { actions as proofsActions } from 'src/features/proofs';

import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { useAppDispatch } from 'src/store';

import { IEntry } from 'src/ts/interfaces';

export const buildReviewRoute = (entry: IEntry | null): string | null => {
  if (!entry) {
    return null;
  }

  if (entry.workflow === 'specialty_income') {
    return entry.specialty_review_item_id
      ? `/my-work/specialty-income-calc/${entry.specialty_review_item_id}/${entry.id}`
      : null;
  }

  if (entry.workflow === 'income_verification') {
    return entry.income_verification_review_id
      ? `/my-work/incomeVerification/${entry.income_verification_review_id}/${entry.id}`
      : null;
  }

  if (entry.workflow === 'asset_verification') {
    return entry.asset_verification_review_item_id
      ? `/my-work/asset-verification/${entry.asset_verification_review_item_id}/${entry.id}`
      : null;
  }

  return `/my-work/${entry.folder_id}`;
};

export const useEntryAssignment = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  const user = useSelector(selectors.user);
  const assignedEntry = useSelector(myWorkSelectors.assignedEntry);
  const isLoadingPendingSubmission = useSelector(myWorkSelectors.isLoadingPendingSubmission);
  const isLoadingSubmissionRequest = useSelector(myWorkSelectors.isLoadingSubmissionRequest);
  const isRequestEnqueued = useSelector(myWorkSelectors.requestEnqueued);
  const [isEntryRequested, setIsEntryRequested] = useState(false);

  const [reviewAssignmentChannel] = useChannel(`review_assignment:${user?.id}`);
  const [requestCount, setRequestCount] = useState(0);
  const hasSpecialtyQueueAccess = useSpecialtyQueueAccess();

  const requestEntry = () => {
    dispatch(proofsActions.reset());
    dispatch(requestReviewEntryThunk());
    setIsEntryRequested(true);
  };

  const requestPendingReview = () => {
    dispatch(getPendingReviewEntryThunk());

    if (hasSpecialtyQueueAccess) {
      dispatch(getSpecialtyQueuePendingCountThunk());
    }
  };

  const cancelRequest = () => {
    dispatch(cancelReviewEntryRequestThunk());
    setIsEntryRequested(false);
  };

  const navigateToSubmission = (assignedEntry: IEntry | null) => {
    const route = buildReviewRoute(assignedEntry);

    if (route) {
      navigate(route);
    } else if (assignedEntry) {
      showSnackbar(
        VariantType.error,
        'The assigned review is missing its route information',
        SnackTypes.none
      );
    }
  };

  useEffect(() => {
    if (!reviewAssignmentChannel) return;

    const channel = reviewAssignmentChannel?.on(REVIEW_ASSIGNED_EVENT, (response) => {
      if (!response) return;

      setIsEntryRequested(false);
      const entry = response.data as IEntry;
      dispatch(myWorkActions.setAssignedEntry(entry));
      navigateToSubmission(entry);
    });

    // stop listening before the component unmounts
    return () => {
      reviewAssignmentChannel?.off(REVIEW_ASSIGNED_EVENT, channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewAssignmentChannel]);

  useEffect(() => {
    if (!isLoadingSubmissionRequest && isEntryRequested && !isRequestEnqueued) {
      setIsEntryRequested(false);
      navigateToSubmission(assignedEntry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    SnackTypes.none,
    VariantType,
    isLoadingSubmissionRequest,
    isEntryRequested,
    showSnackbar,
    isRequestEnqueued
  ]);

  useEffect(() => {
    if (!isLoadingSubmissionRequest && !isLoadingPendingSubmission && isRequestEnqueued) {
      if (assignedEntry) {
        setIsEntryRequested(false);
        navigateToSubmission(assignedEntry);
      } else {
        const timerId = setTimeout(() => {
          setRequestCount((prevCount) => prevCount + 1);
          requestPendingReview();
        }, 5000);

        return () => {
          clearTimeout(timerId);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequestEnqueued, requestCount, assignedEntry]);

  return { requestEntry, cancelRequest, isLoadingSubmissionRequest };
};
